/**
 * RNR fix (2026-07-26, rev 5): colour swatch slider inside Flickity
 * carousels (homepage featured collections, mega menu, related products).
 *
 * Symptoms fixed:
 *  - Mobile: swiping the colour strip dragged the whole carousel.
 *  - Desktop: mouse drag on the colours did nothing.
 *  - Desktop: drifting a few px off the card mid-drag fired the theme's
 *    mouseleave handler, closing the strip and re-enabling carousel drag,
 *    so a held gesture suddenly advanced the products.
 *  - Trackpad: two-finger horizontal scroll over the colours was captured
 *    by the carousel's wheel handler and advanced to the next products.
 *  - Mobile: the '+ N Colours' toggle is a ~13px-tall text target, so taps
 *    frequently hit the product link instead and navigated away.
 *
 * Root causes:
 *  - theme.js's '.more-colors' handler calls Flickity.data(slider) to toggle
 *    carousel dragging, but the bundle exports no global Flickity, so it
 *    threw and drag stayed bound.
 *  - .flickity-viewport forces touch-action: pan-y over the strip.
 *  - Browsers do not natively drag-scroll an overflow div with the mouse.
 *  - Theme closes the strip + re-enables drag on card mouseleave mid-drag.
 *  - A carousel-level wheel handler consumes horizontal wheel deltas.
 *  - Tiny tap target on the colours toggle.
 */
(function () {
  'use strict';

  if (!window.Flickity) {
    window.Flickity = {
      data: function (el) {
        return (el && el.flickityInstance) || null;
      }
    };
  }

  function closestOpenStrip(target) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest('product-grid-item-swatch.showswatchs');
  }

  // Keep Flickity from hijacking gestures that start inside an open strip
  ['pointerdown', 'touchstart', 'mousedown'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var strip = closestOpenStrip(e.target);
      if (strip && strip.closest('.flickity-enabled')) {
        e.stopPropagation();
      }
    }, true);
  });

  // Trackpad/mouse wheel: horizontal scroll over an open strip scrolls the
  // colours, never the carousel. Vertical scroll passes through to the page.
  document.addEventListener('wheel', function (e) {
    var strip = closestOpenStrip(e.target);
    if (!strip) return;
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.stopPropagation();
      e.preventDefault();
      strip.scrollLeft += e.deltaX;
    }
  }, {capture: true, passive: false});

  // Mouse drag-to-scroll (touch uses native pan-x scrolling)
  var drag = null;
  var suppressClick = false;

  document.addEventListener('pointerdown', function (e) {
    var strip = closestOpenStrip(e.target);
    if (!strip) return;
    drag = {
      strip: strip,
      item: strip.closest('.product-grid-item'),
      startX: e.clientX,
      startScroll: strip.scrollLeft,
      moved: false,
      scrollbar: e.target === strip && e.offsetY >= strip.clientHeight,
      mouse: e.pointerType === 'mouse' && e.button === 0
    };
  }, true);

  document.addEventListener('pointermove', function (e) {
    if (!drag || !drag.mouse || drag.scrollbar) return;
    var dx = e.clientX - drag.startX;
    if (!drag.moved && Math.abs(dx) > 4) {
      drag.moved = true;
      drag.strip.style.cursor = 'grabbing';
    }
    if (drag.moved) {
      drag.strip.scrollLeft = drag.startScroll - dx;
      e.preventDefault();
    }
  }, true);

  ['pointerup', 'pointercancel'].forEach(function (type) {
    document.addEventListener(type, function () {
      if (!drag) return;
      drag.strip.style.cursor = '';
      if (drag.moved) {
        suppressClick = true;
        setTimeout(function () { suppressClick = false; }, 50);
      }
      drag = null;
    }, true);
  });

  // After a real drag, swallow the click so it does not select a swatch
  document.addEventListener('click', function (e) {
    if (suppressClick && closestOpenStrip(e.target)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  // Keep the theme's mouseleave handler from closing the strip mid-gesture
  document.addEventListener('mouseleave', function (e) {
    var el = e.target;
    if (!el || typeof el.matches !== 'function') return;
    if (!el.matches('.product-grid-item')) return;
    var strip = el.querySelector('product-grid-item-swatch.showswatchs');
    if (!strip) return;
    if (drag && drag.item === el) {
      e.stopPropagation();
      return;
    }
    var r = strip.getBoundingClientRect();
    if (typeof e.clientY === 'number' &&
        e.clientY >= r.top - 10 && e.clientY <= r.bottom + 60 &&
        e.clientX >= r.left - 40 && e.clientX <= r.right + 40) {
      e.stopPropagation();
    }
  }, true);

  // CSS hardening + bigger touch target for the '+ N Colours' toggle
  var style = document.createElement('style');
  style.textContent =
    '.flickity-enabled product-grid-item-swatch.product-grid-item__swatch.showswatchs{' +
    'touch-action:pan-x !important;' +
    'overflow-x:auto !important;' +
    'overflow-y:hidden !important;' +
    '-webkit-overflow-scrolling:touch;' +
    'overscroll-behavior-x:contain;' +
    '}' +
    'product-grid-item-swatch.product-grid-item__swatch.showswatchs{cursor:grab;}' +
    '@media (pointer: coarse){' +
    '.product-grid-item .more-colors{' +
    'position:relative;z-index:2;display:inline-block;' +
    '}' +
    '.product-grid-item .more-colors::after{' +
    'content:"";position:absolute;top:-15px;bottom:-15px;left:-10px;right:-12px;' +
    '}' +
    '}';
  document.head.appendChild(style);
})();

/* =====================================================================
 * RNR fix (2026-07-28): mobile drawer mega-menu — dead tabs, no scrolling,
 * collection names clipped out of view.
 *
 * Root causes:
 *  1. The mobile drawer shows the first nav panel via CSS (.first-item /
 *     .is-open-newmenu) but interactivity was tied to .is-visible, which
 *     only desktop hover-disclosure sets and iOS-synthesized mouseleave
 *     events strip. .mega-menu-layout then stays at pointer-events:none,
 *     so the Categories/Featured/Colour tabs and tiles were untappable.
 *  2. theme.js HoverDisclosure.handleTablets binds touchstart on the whole
 *     .menu__item (which contains the dropdown) and preventDefaults any
 *     tap while .is-visible is absent, swallowing taps AND scroll
 *     gestures inside the panel.
 *  3. Flickity measures the menu carousels while the drawer is hidden, so
 *     the viewport height was too short on mobile and the collection
 *     names rendered clipped below the viewport's overflow:hidden edge.
 * ===================================================================== */
(function () {
  'use strict';

  var MOBILE = '(max-width: 1024px)';

  function isMobile() {
    return window.matchMedia(MOBILE).matches;
  }

  // (1) Keep the open drawer interactive regardless of .is-visible
  var style = document.createElement('style');
  style.textContent =
    '@media only screen and ' + MOBILE + '{' +
    '.is-open-newmenu nav#NavStandard .mega-menu-layout,' +
    '.is-open-newmenu nav#NavStandard .mega-tab-content.active{' +
    'pointer-events:auto;' +
    '}' +
    '}';
  document.head.appendChild(style);

  function init() {
    // (2) Taps that start inside dropdown content must not reach the
    // HoverDisclosure touchstart handler on .menu__item. Taps on the
    // top-level links (outside [data-hover-disclosure]) keep the existing
    // open/switch behaviour.
    document.querySelectorAll('#NavStandard [data-hover-disclosure]').forEach(function (dd) {
      dd.addEventListener('touchstart', function (e) {
        if (isMobile()) e.stopPropagation();
      }, { passive: true });
    });

    // (3) Re-measure the menu carousels when the drawer opens (and again
    // after its open transition) so viewport heights include the titles.
    function resizeMenuCarousels() {
      if (!window.Flickity) return;
      document.querySelectorAll('#NavStandard .flickity-enabled').forEach(function (el) {
        var flkty = window.Flickity.data(el);
        if (flkty && typeof flkty.resize === 'function') flkty.resize();
      });
    }

    document.querySelectorAll('.opennewdrawer').forEach(function (btn) {
      btn.addEventListener('click', function () {
        [100, 500].forEach(function (delay) {
          setTimeout(resizeMenuCarousels, delay);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* =====================================================================
 * RNR fix (2026-07-28, rev 2): product tiles flashed a coloured block
 * (the photo's average colour, pinkish on leather products) while images
 * lazyload. Client wants the loading state to stay white.
 *
 * Cause: product-grid-item.liquid sets an inline LQIP placeholder
 * `background-image: url({{ image | img_url: '1x1' }})` on the media
 * containers (.lazy-image). The 1-pixel image stretches into a flat
 * colour swatch until lazysizes swaps in the real image.
 *
 * The real image renders in a child [data-bgset] div, so removing the
 * placeholder background on the container only affects the loading
 * state. Inline style requires !important to override.
 * ===================================================================== */
(function () {
  'use strict';

  var style = document.createElement('style');
  style.textContent =
    '.lazy-image:not([data-bgset]):not([data-srcset]){' +
    'background-image:none !important;' +
    'background-color:var(--bg, #FBFBFC);' +
    '}';
  document.head.appendChild(style);
})();
